import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { IFiltersState } from '../store/filter.reducer';
import { Observable } from 'rxjs/Observable';
import { FILTER_ACTIONS } from '../store/filter.actions';
import { Categories, Filter } from '../classes/filter';
import { map, switchMap } from 'rxjs/operators';
import { FilterUtils } from '../utils/filter-utils';

@Injectable()
export class FilterActionCreatorService {
  // df form store
  private formStore: any;

  constructor(private readonly store: Store<any>) {
    this.store = store.select('filters') as Store<IFiltersState>;
    this.formStore = store.select('forms');
  }

  public getState(props: Array<keyof IFiltersState> = []) {
    let state: any = this.store;

    for (const prop of props) {
      state = state.select(prop);
    }

    return state;
  }

  public getCurrentSelectedFilter(filterTypeAndSubtype?: string): Observable<{
    filterTypeAndSubtype: string,
    filter: Filter,
    categories: Categories | null,
  }> {
    if (filterTypeAndSubtype) {
      return this.store.select('currentSelected')
        .take(1)
        .map((data) => {
          if (!data.has(filterTypeAndSubtype)) {
            return {
              filterTypeAndSubtype: filterTypeAndSubtype,
              filter: null,
              categories: null
            };
          } else {
            const selectedFilter = data.get(filterTypeAndSubtype);

            return {
              filterTypeAndSubtype: filterTypeAndSubtype,
              filter: selectedFilter.filter,
              categories: selectedFilter.categories
            };
          }
        });
    } else {
      return this.getState()
        .take(1)
        .map((filterState) => {
          const filterTypeAndSubtype_ = filterState.current;

          if (filterTypeAndSubtype_) {
            if (!filterState.currentSelected.has(filterTypeAndSubtype_)) {
              return {
                filterTypeAndSubtype: filterTypeAndSubtype_,
                filter: null,
                categories: null
              };
            } else {
              const selectedFilter = filterState.currentSelected.get(filterTypeAndSubtype_);

              return {
                filterTypeAndSubtype: filterTypeAndSubtype_,
                filter: selectedFilter.filter,
                categories: selectedFilter.categories
              };
            }
          }
        });
    }
  }

  public getCurrentAppliedFilters(key: string): Observable<{ key: string, data: Map<string, Filter> | null }> {
    return this.store.select('applied')
        .take(1)
        .map(data => {
          if (!data.has(key)) {
              return { key, data: null };
          } else {
              return { key, data: data.get(key) };
          }
        });
  }

  public getCurrentAppliedFiltersContinuously(injectedKey: string): Observable<{ key: string, data: Map<string, Filter> | null }> {
    return this.store.select('current')
        .pipe(
          switchMap(key => {
            key = key || injectedKey;
            return this.store.select('applied').pipe(
              map(data => {
              if (!data.has(key)) {
                  return { key, data: null };
              } else {
                  return { key, data: data.get(key) };
              }
            })
          );
        })
        );
  }

  public getAppliedFilterProperties(): Observable<any[] | null> {
    return this.getState(['current'])
      .flatMap((current: string) => {
        return this.store.select('appliedFilterProperties')
          .map(data => {
            if (!data.has(current)) {
              return null;
            } else {
              return data.get(current);
            }
          });
      });
  }

  public getCurrentSelectedFilterQuery(currentFilterTypeAndSubtype: string): Observable<any | null> {
    return this.store.select('currentSelectedQuery')
      // .take(1)
      .map(currentSelectedQueryRes => {
        if (!currentSelectedQueryRes) {
          return null;
        }
        if (!currentSelectedQueryRes.has(currentFilterTypeAndSubtype)) {
          return null;
        }

        return currentSelectedQueryRes.get(currentFilterTypeAndSubtype);
      });
  }

  public getCurrentSelectedFilterConfiguration(currentFilterTypeAndSubtype: string): Observable<any | null> {
    return this.store.select('currentSelectedConfig')
    // .take(1)
      .map(currentSelectedConfigRes => {
        if (!currentSelectedConfigRes) { return null; }

        const currentSelectedConfig = currentSelectedConfigRes.get(currentFilterTypeAndSubtype);
        if (!currentSelectedConfig) { return null; }
        return currentSelectedConfig;
      });
  }

  /**
   * return the last stored filter's form-data
   */
  public getCurrentSelectedFormData(currentFilterTypeAndSubtype: string): Observable<any | null> {
    return this.store.select('currentSelectedFormData')
      .map(currentSelectedFormDataRes => {
        let currentSelectedFormData;
        if (currentSelectedFormDataRes) {
          currentSelectedFormData = currentSelectedFormDataRes.get(currentFilterTypeAndSubtype);
        }
        return currentSelectedFormData;
      });
  }

  public storeState() {
    this.store.dispatch({
      type: FILTER_ACTIONS.STORE_STATE

    });
  }

  public restoreState() {
    this.store.dispatch({
      type: FILTER_ACTIONS.RESTORE_STATE
    });
  }

  public areFiltersInCache(): boolean {
    return !!sessionStorage.getItem('filters');
  }

  public setFilterType(filterType: string, filterSubtype: string) {
    const filterTypeAndSubtype = FilterUtils.buildFilterTypes(filterType, [filterSubtype]);

    this.store.dispatch({
      type: FILTER_ACTIONS.SET_CURRENT,
      payload: {
        filterTypeAndSubtype: filterTypeAndSubtype
      }
    });
  }

  public setCurrentSelectedTab(currentSelectedTab: string) {
    this.store.dispatch({
      type: FILTER_ACTIONS.SET_CURRENT_SELECTED_TAB,
      payload: {
        currentSelectedTab: currentSelectedTab
      }
    });
  }

  public setCurrentFilters(
    filterType: string, filterSubtype: string, defaultFilterSubtype: string, filters: Filter[],
    categories: any, defaultFilterQueryObj: any, formFilter: any, filterConfig: any, filterFormData: any
  ): void {
    const filtersMap = new Map<string, Filter>();
    filters.map(filter => filtersMap.set(filter.id, filter));

    const defaultFilters = filters.filter(filter => filter.default === true);
    const defaultFilter = (defaultFilters && defaultFilters.length > 0) ? defaultFilters[0] : undefined;

    const filterTypeAndSubtype = FilterUtils.buildFilterTypes(filterType, [filterSubtype]);
    const filterTypeAndDefaultFilterSubtype = FilterUtils.buildFilterTypes(filterType, [defaultFilterSubtype]);

    this.store.dispatch({
      type: FILTER_ACTIONS.SET_CURRENT_FILTERS,
      payload: {
        filterType: filterTypeAndSubtype,
        defaultFilterType: filterTypeAndDefaultFilterSubtype,
        filters: filtersMap,
        defaultFilter: defaultFilter,
        categories: categories,
        defaultFilterQueryObj: defaultFilterQueryObj,
        formFilter: formFilter,
        filterConfig: filterConfig,
        filterFormData: filterFormData
      }
    });
  }

  public setAppliedFilterProperties(appliedFilterProperties: any[], filterTypeAndSubtype?: string) {
    this.store.dispatch({
      type: FILTER_ACTIONS.SET_APPLIED_FILTER_PROPERTIES,
      payload: {
        filterTypeAndSubtype: filterTypeAndSubtype ? filterTypeAndSubtype : '',
        appliedFilterProperties: appliedFilterProperties
      }
    });
  }

  public setAppliedFilter(filterToUpdate: Filter, categories?: any) {
    this.store.dispatch({
      type: FILTER_ACTIONS.SET_APPLIED_FILTER,
      payload: {
        filter: filterToUpdate,
        categories: categories
      }
    });
  }

  public setCurrentSelectedFilter(currentSelectedFilter: any) {
    this.store.dispatch({
      type: FILTER_ACTIONS.SET_SELECTED_FILTER,
      payload: {
        filterTypeAndSubtype: currentSelectedFilter.filterTypeAndSubtype,
        filter: currentSelectedFilter.filter,
        categories: currentSelectedFilter.categories
      }
    });
  }

  public setCurrentSelectedQuery(filterType: string, filterSubtype: string, query: any, formData: any) {
    const filterTypeAndSubtype = FilterUtils.buildFilterTypes(filterType, [filterSubtype]);

    this.store.dispatch({
      type: FILTER_ACTIONS.SET_SELECTED_QUERY,
      payload: {
        filterTypeAndSubtype: filterTypeAndSubtype,
        query: query,
        formData: formData
      }
    });
  }

  public deleteAppliedFilter(filterId: string) {
    this.store.dispatch({
      type: FILTER_ACTIONS.DELETE_APPLIED_FILTER,
      payload: {
        filterId: filterId
      }
    });
  }
}
